import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function MercedCountyFloodSeasonPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Merced County Flood Season Guide | Indoor-Restore';

    // Meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      'content',
      'Central Valley rainy seasons are intensifying. Learn how Merced County homeowners can prepare for flood season, protect their property, and respond fast when water damage strikes.'
    );

    // Meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      'content',
      'Merced County flood season, Central Valley flooding, flood preparedness Merced CA, water damage prevention, flood readiness homeowners'
    );

    // Canonical link — use querySelector to avoid duplicates
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://mercedwaterrestoration.com/blog/merced-county-flood-season');

    // JSON‑LD scripts
    const addJsonLd = (obj) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(obj);
      document.head.appendChild(script);
      return script;
    };

    const blogPostingScript = addJsonLd({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Merced County Flood Season: What Homeowners Need to Know",
      "description":
        "Central Valley rainy seasons are intensifying. Learn how Merced County homeowners can prepare for flood season, protect their property, and respond fast when water damage strikes.",
      "image":
        "https://public.readdy.ai/ai/img_res/b0a0dca7f105dbcebafb633ed3b9184a.jpg",
      "datePublished": "2025-04-15T08:00:00-07:00",
      "dateModified": "2025-04-15T08:00:00-07:00",
      "author": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "url": "https://mercedwaterrestoration.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "logo": {
          "@type": "ImageObject",
          "url": "https://mercedwaterrestoration.com/wp-content/uploads/2024/11/Indoor-Restore-Services-Logo.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://mercedwaterrestoration.com/blog/merced-county-flood-season"
      },
      "keywords":
        "Merced County flood season, Central Valley flooding, flood preparedness, water damage prevention"
    });

    const faqScript = addJsonLd({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "When is flood season in Merced County, California?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Merced County's primary flood season runs from November through March, coinciding with California's wet season. However, atmospheric river events — which have become more frequent and intense — can bring significant flooding outside this window. The San Joaquin River and its tributaries are the primary flood risk for Merced County residents."
          }
        },
        {
          "@type": "Question",
          "name": "Is Merced County at high risk for flooding?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Yes. Much of Merced County sits in the San Joaquin Valley floodplain, making it one of the higher flood-risk counties in California. FEMA flood maps designate significant portions of the county — particularly near the San Joaquin River, Bear Creek, and Merced River — as Special Flood Hazard Areas (SFHA). Homeowners in these zones are typically required to carry flood insurance."
          }
        },
        {
          "@type": "Question",
          "name": "Does homeowner's insurance cover flood damage in Merced?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "Standard homeowner's insurance policies do NOT cover flood damage. Flood coverage must be purchased separately through the National Flood Insurance Program (NFIP) or a private flood insurer. However, water damage from internal sources — such as burst pipes, appliance leaks, or roof leaks — is typically covered by standard homeowner's policies."
          }
        },
        {
          "@type": "Question",
          "name": "What should I do immediately after my home floods in Merced?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text":
              "First, ensure everyone's safety and do not enter standing water if there is any risk of electrical hazards. Then: (1) Call your insurance company to report the claim, (2) Document all damage with photos and video before touching anything, (3) Call a professional water damage restoration company like Indoor-Restore Services immediately — the 24–48 hour window is critical for preventing mold growth, (4) Remove valuables and irreplaceable items if it is safe to do so."
          }
        }
      ]
    });

    const localBusinessScript = addJsonLd({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Indoor-Restore Services",
      "description":
        "24/7 emergency water damage restoration, flood cleanup, and mold remediation serving Merced County, CA.",
      "telephone": "+12094512288",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Merced",
        "addressRegion": "CA",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 37.3022,
        "longitude": -120.4830
      },
      "areaServed": {
        "@type": "County",
        "name": "Merced County",
        "addressRegion": "CA"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      }
    });

    // Cleanup on unmount
    return () => {
      [blogPostingScript, faqScript, localBusinessScript].forEach((s) => {
        if (s && s.parentNode) document.head.removeChild(s);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100 pt-24 pb-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-teal-600 transition-colors">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/blog" className="hover:text-teal-600 transition-colors">
              Blog
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-gray-900 font-medium">Merced County Flood Season</span>
          </nav>
        </div>
      </div>

      {/* Article */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="mb-6">
            <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
              Local News
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Merced County Flood Season: What Homeowners Need to Know
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                April 15, 2025
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                5 min read
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line"></i>
                Merced County, CA
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full h-96 rounded-xl overflow-hidden mb-10 shadow-lg">
            <img
              src="https://readdy.ai/api/search-image?query=flooded%20suburban%20street%20in%20Central%20Valley%20California%20with%20water%20rushing%20past%20homes%20overcast%20stormy%20sky%20dramatic%20wide%20angle%20photography%20residential%20neighborhood&width=1200&height=630&seq=merced-flood-hero-v1&orientation=landscape"
              alt="Flooded street in Merced County, California during rainy season"
              title="Merced County flood season — Central Valley residential flooding"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong>Merced County sits in the heart of California's San Joaquin Valley</strong> — one of the most productive agricultural regions in the world, and one of the most flood‑prone. With atmospheric river events becoming more frequent and intense, Central Valley homeowners are facing a new reality: <strong>flood season is no longer predictable, and preparation is no longer optional.</strong>
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              At <strong>Indoor-Restore Services</strong>, we respond to flood damage emergencies across Merced County year‑round. In this guide, we'll walk you through everything local homeowners need to know — from understanding your flood risk and insurance coverage, to the critical steps you must take in the first hours after water enters your home.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-map-2-line text-teal-600"></i>
              Understanding Merced County's Flood Risk
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              Merced County's geography makes it uniquely vulnerable to flooding. The county is crisscrossed by several major waterways — including the <strong>San Joaquin River, Merced River, Bear Creek, and Black Rascal Creek</strong> — all of which can overflow their banks during heavy rainfall or rapid snowmelt from the Sierra Nevada.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              FEMA flood maps designate large portions of the county as <strong>Special Flood Hazard Areas (SFHA)</strong> — zones with a 1% or greater annual chance of flooding (commonly called the "100‑year floodplain"). If your home is in an SFHA and you have a federally backed mortgage, you are legally required to carry flood insurance.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              But even homeowners outside designated flood zones are at risk. In recent years, <strong>atmospheric river storms</strong> have delivered rainfall totals that overwhelm drainage infrastructure and cause flooding in areas with no prior flood history. The 2023 storms that devastated parts of the Central Valley were a stark reminder that flood risk maps don't tell the whole story.
            </p>

            {/* Risk Callout */}
            <div className="bg-teal-50 border-l-4 border-teal-500 rounded-lg p-5 mb-8">
              <p className="text-teal-800 text-sm font-semibold flex items-start gap-2">
                <i className="ri-information-line text-lg mt-0.5 shrink-0"></i>
                <span><strong>Local Fact:</strong> According to FEMA, Merced County has received over $50 million in flood insurance claims over the past two decades. The San Joaquin Valley's flat terrain means floodwaters spread quickly and widely — a few inches of rain can translate to feet of standing water in low‑lying neighborhoods.</span>
              </p>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-calendar-event-line text-teal-600"></i>
              When Is Flood Season in Merced County?
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              Merced County's traditional flood season runs from <strong>November through March</strong>, coinciding with California's wet season. However, the definition of "flood season" has expanded significantly in recent years:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3">
                  <i className="ri-cloud-windy-line text-teal-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Nov – Mar</h3>
                <p className="text-gray-500 text-xs leading-relaxed">Primary wet season — atmospheric rivers, sustained rainfall, and river overflow</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-3">
                  <i className="ri-temp-hot-line text-amber-600 text-lg"></i>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Mar – May</h3>
                <p className="text-gray-500 text-xs leading-relaxed">Sierra snowmelt season — rivers run high and can overflow with little warning</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-3">
                  <i className="ri-thunderstorms-line text-red-500 text-lg"></i>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">Year‑Round</h3>
                <p className="text-gray-500 text-xs leading-relaxed">Atmospheric river events can strike any month — 2023 proved no season is truly safe</p>
              </div>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-shield-check-line text-teal-600"></i>
              5 Steps to Prepare Your Home Before Flood Season
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Preparation is the single most effective way to reduce flood damage costs. Here's what Merced County homeowners should do before the rains arrive:
            </p>

            <div className="space-y-5 mb-8">
              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">1</div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Know Your Flood Zone</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Visit <strong>FEMA's Flood Map Service Center</strong> (msc.fema.gov) and enter your address to see your official flood zone designation. If you're in Zone AE or AO, you're in a high‑risk area. Even Zone X properties have experienced flooding in recent years — don't assume you're safe.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">2</div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Review Your Insurance Coverage</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    <strong>Standard homeowner's insurance does not cover flood damage.</strong> Contact your insurance agent to discuss adding flood coverage through the National Flood Insurance Program (NFIP) or a private insurer. Note: NFIP policies have a 30‑day waiting period before they take effect — don't wait until a storm is forecast.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">3</div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Inspect and Clear Drainage</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Clean gutters, downspouts, and yard drains before the rainy season. Ensure downspouts direct water at least 6 feet away from your foundation. Check that your property grading slopes away from the house — water pooling against your foundation is a major entry point.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">4</div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">Elevate and Protect Valuables</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Move important documents, electronics, and irreplaceable items to upper floors or waterproof containers. Consider elevating your water heater, HVAC equipment, and electrical panel if they are located in flood‑prone areas of your home. A sump pump with a battery backup is a worthwhile investment for homes with basements or low‑lying crawl spaces.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full text-sm font-bold shrink-0 mt-0.5">5</div>
                <div>
                  {/* Fixed typo: corrected className attribute */}
                  <h3 className="text-base font-bold text-gray-900 mb-1">Have an Emergency Plan Ready</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Know your evacuation routes, keep a 72‑hour emergency kit stocked, and save the number of a <strong>24/7 water damage restoration company</strong> in your phone before you need it. When flooding strikes, every hour counts — having a plan eliminates the frantic search for help during a crisis.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-alarm-warning-line text-teal-600"></i>
              What to Do in the First Hours After Flooding
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              If water enters your home, the clock starts immediately. <strong>Mold can begin growing within 24–48 hours</strong> of water exposure, and structural damage compounds with every hour water remains in contact with building materials. Here's the priority order:
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
              <h3 className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-amber-600"></i>
                Immediate Safety First
              </h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-amber-600"></i>
                  <span>Do not enter standing water if there is any risk of electrical hazards — turn off power at the breaker first</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-amber-600"></i>
                  <span>Do not drink tap water until authorities confirm it is safe — floodwater can contaminate municipal supplies</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-amber-600"></i>
                  <span>Wear rubber boots and gloves when entering flood‑affected areas — floodwater is often contaminated with sewage and chemicals</span>
                </li>
              </ul>
            </div>

            <div className="space-y-3 mb-8">
              {[
                { step: '1', title: 'Document Everything', desc: 'Before touching or moving anything, photograph and video every affected area. This documentation is essential for your insurance claim and restoration estimate.' },
                { step: '2', title: 'Call Your Insurance Company', desc: 'Report the claim immediately. Ask about emergency living expenses if your home is uninhabitable, and confirm what documentation they require.' },
                { step: '3', title: 'Call a Professional Restoration Company', desc: 'Contact Indoor-Restore Services at (209) 451-2288. Our team deploys 24/7 and will begin water extraction and structural drying immediately — stopping the damage clock.' },
                { step: '4', title: 'Remove Valuables Safely', desc: 'Once it is safe to do so, move undamaged belongings out of affected areas. Do not attempt to dry the structure yourself — improper drying leads to hidden mold and structural damage.' },
              ].map(item => (
                <div key={item.step} className="flex gap-4 items-start bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="w-7 h-7 flex items-center justify-center bg-teal-600 text-white rounded-full text-xs font-bold shrink-0 mt-0.5">{item.step}</div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-0.5">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Section 5 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-file-list-3-line text-teal-600"></i>
              Flood Insurance vs. Homeowner's Insurance: Know the Difference
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              One of the most common — and costly — misconceptions among Merced County homeowners is assuming their standard homeowner's policy covers flood damage. <strong>It does not.</strong> Here's a clear breakdown:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-teal-50 rounded-xl p-5 border border-teal-100">
                <h3 className="text-sm font-bold text-teal-800 mb-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-600"></i>
                  Covered by Standard Homeowner's Policy
                </h3>
                <ul className="text-gray-700 text-sm space-y-1.5">
                  <li className="flex items-start gap-2"><i className="ri-check-line text-teal-600 mt-0.5 shrink-0"></i> Burst pipes and plumbing failures</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line text-teal-600 mt-0.5 shrink-0"></i> Appliance leaks (washing machine, dishwasher)</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line text-teal-600 mt-0.5 shrink-0"></i> Roof leaks from storm damage</li>
                  <li className="flex items-start gap-2"><i className="ri-check-line text-teal-600 mt-0.5 shrink-0"></i> Water damage from firefighting efforts</li>
                </ul>
              </div>
              <div className="bg-red-50 rounded-xl p-5 border border-red-100">
                <h3 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
                  <i className="ri-close-circle-fill text-red-500"></i>
                  NOT Covered — Requires Separate Flood Policy
                </h3>
                <ul className="text-gray-700 text-sm space-y-1.5">
                  <li className="flex items-start gap-2"><i className="ri-close-line text-red-500 mt-0.5 shrink-0"></i> River or creek overflow into your home</li>
                  <li className="flex items-start gap-2"><i className="ri-close-line text-red-500 mt-0.5 shrink-0"></i> Storm surge or surface water flooding</li>
                  <li className="flex items-start gap-2"><i className="ri-close-line text-red-500 mt-0.5 shrink-0"></i> Mudslides and debris flows</li>
                  <li className="flex items-start gap-2"><i className="ri-close-line text-red-500 mt-0.5 shrink-0"></i> Groundwater seeping through foundation</li>
                </ul>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              If you're unsure whether your damage is covered, call your insurance agent first — then call us. Indoor-Restore Services works directly with both homeowner's insurance and flood insurance adjusters to ensure your claim is documented correctly and you receive the maximum coverage you're entitled to.
            </p>

            {/* Section 6 - FAQ */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-question-line text-teal-600"></i>
              Frequently Asked Questions
            </h2>

            <div className="space-y-4 mb-8">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-base font-bold text-gray-900 mb-2">When is flood season in Merced County, California?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Merced County's primary flood season runs from November through March. However, atmospheric river events and Sierra snowmelt (March–May) can cause flooding outside this window. Recent years have shown that no month is entirely safe from significant flooding in the Central Valley.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-base font-bold text-gray-900 mb-2">Is Merced County at high risk for flooding?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Yes. Much of Merced County sits in the San Joaquin Valley floodplain. FEMA flood maps designate significant portions of the county — particularly near the San Joaquin River, Bear Creek, and Merced River — as Special Flood Hazard Areas. Homeowners in these zones are typically required to carry flood insurance.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-base font-bold text-gray-900 mb-2">Does homeowner's insurance cover flood damage in Merced?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  No. Standard homeowner's insurance does not cover flood damage from external water sources. Flood coverage must be purchased separately through the NFIP or a private insurer. Internal water damage (burst pipes, appliance leaks) is typically covered by standard policies.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-base font-bold text-gray-900 mb-2">What should I do immediately after my home floods in Merced?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Ensure safety first (no electrical hazards, no contaminated water contact). Then: document all damage with photos, call your insurance company, and immediately contact a professional restoration company like Indoor-Restore Services at (209) 451-2288. The 24–48 hour window is critical for preventing mold growth.
                </p>
              </div>
            </div>

            {/* Closing */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-home-heart-line text-teal-600"></i>
              Your Local Flood Response Partner in Merced County
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Flood season in Merced County is a reality every homeowner must prepare for. The good news: with the right preparation, the right insurance coverage, and the right restoration partner on speed dial, you can dramatically reduce the impact of even a serious flood event.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Indoor-Restore Services</strong> has been responding to flood and water damage emergencies across Merced County for years. Our IICRC‑certified technicians are available <strong>24 hours a day, 7 days a week</strong> — because flooding doesn't wait for business hours. We handle everything from emergency water extraction and structural drying to mold remediation and full reconstruction, and we work directly with your insurance company to make the process as smooth as possible.
            </p>

            <p className="text-gray-700 leading-relaxed">
              <strong>Don't wait until the water is rising.</strong> Save our number now:{' '}
              <a href="tel:2094512288" className="text-teal-600 font-semibold hover:text-teal-700 underline">
                (209) 451-2288
              </a>
              . When flood season hits Merced County, we'll be ready — and so will you.
            </p>

          </div>
        </div>
      </article>

      {/* Emergency CTA */}
      <section className="bg-teal-700 py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Flood Damage in Merced County? We Respond 24/7.</h3>
          <p className="text-teal-100 mb-6 text-sm">
            IICRC‑certified flood cleanup and water damage restoration across Merced County — available around the clock.
          </p>
          <a
            href="tel:2094512288"
            className="inline-flex items-center gap-2 bg-white text-teal-700 px-8 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line"></i>
            Call 24/7: (209) 451-2288
          </a>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Related Articles</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Link
              to="/blog/water-damage-first-24-hours"
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
            >
              <div className="w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=flooded%20living%20room%20interior%20with%20water%20on%20hardwood%20floor%20furniture%20damaged%20homeowner%20assessing%20water%20damage%20natural%20light%20realistic%20photography&width=400&height=250&seq=blog1-indoor&orientation=landscape"
                  alt="What to Do in the First 24 Hours After Water Damage"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">Water Damage</span>
                <h4 className="text-sm font-bold text-gray-900 mb-1 leading-snug group-hover:text-teal-700 transition-colors">
                  What to Do in the First 24 Hours After Water Damage
                </h4>
              </div>
            </Link>

            <Link
              to="/blog/homeowners-insurance-claim-water-damage"
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
            >
              <div className="w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=insurance%20agent%20and%20homeowner%20reviewing%20documents%20at%20kitchen%20table%20after%20water%20damage%20claim%20paperwork%20professional%20setting%20warm%20lighting&width=400&height=250&seq=blog3-indoor&orientation=landscape"
                  alt="Navigating Your Homeowner's Insurance Claim for Water Damage"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">Insurance Tips</span>
                <h4 className="text-sm font-bold text-gray-900 mb-1 leading-snug group-hover:text-teal-700 transition-colors">
                  Navigating Your Homeowner's Insurance Claim for Water Damage
                </h4>
              </div>
            </Link>

            <Link
              to="/blog/structural-drying-101"
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group cursor-pointer block"
            >
              <div className="w-full h-44 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=industrial%20air%20movers%20and%20commercial%20dehumidifiers%20arranged%20in%20water%20damaged%20room%20restoration%20process%20professional%20equipment%20drying%20hardwood%20floors%20bright%20interior%20clean&width=400&height=250&seq=structural-drying-hero-v1&orientation=landscape"
                  alt="Structural Drying 101: Why Industrial Equipment Matters"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">Restoration Tips</span>
                <h4 className="text-sm font-bold text-gray-900 mb-1 leading-snug group-hover:text-teal-700 transition-colors">
                  Structural Drying 101: Why Industrial Equipment Matters
                </h4>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
